import AsyncAutocomplete from '@components/v2/form/AsyncAutocomplete/AsyncAutocomplete';
import MoneyField from '@components/v2/form/MoneyField/MoneyField';
import TextField from '@components/v2/form/TextField/TextField';
import { Box, Stack } from '@mui/material';
import { Control, Controller } from 'react-hook-form';
import LangEnum from '../../../../../../../../../lang/LangEnum';
import { ShippingCompany } from '../../../../../../../../../services/modules/shippingCompanies/shippingCompanies.types';
import { formatCNPJ } from '../../../../../../../../../utils/formatters';
import { useTranslation } from '../../../../../../../../translation/TranslationContext';
import { OrderCondition } from '../../../../../../../conditions/interfaces/orderCondition.interface';
import { OrderConditionFieldWithError } from '../../../../../../../conditions/orderConditionFieldErrors.map';
import { getConditionFieldsGridSx, hasFieldError } from '../../orderConditionDetails.utils';
import {
  MIN_DELIVERY_TERM_BUSINESS_DAYS,
  OrderConditionDetailsFormValues,
} from '../orderConditionDetailsForm.schema';

const SHIPPING_COMPANY_LIST_WIDTH = 480;

interface OrderConditionManualFreightFieldsProps {
  condition: OrderCondition;
  control: Control<OrderConditionDetailsFormValues>;
  canEditOrder: boolean;
  containerWidth: number;
  fieldsWithError: Set<OrderConditionFieldWithError>;
  onSave: () => void;
}

const OrderConditionManualFreightFields = ({
  condition,
  control,
  canEditOrder,
  containerWidth,
  fieldsWithError,
  onSave,
}: OrderConditionManualFreightFieldsProps) => {
  const { translate } = useTranslation();
  const gridSx = getConditionFieldsGridSx(true, containerWidth);

  return (
    <Stack sx={{ gap: 2 }}>
      <Box sx={gridSx}>
        <Controller
          name="shippingCompanyId"
          control={control}
          render={({ field, fieldState }) => (
            <AsyncAutocomplete<ShippingCompany>
              source={{ url: '/shipping-companies' }}
              label={translate(LangEnum.GLOBAL_SHIPPING_COMPANY)}
              dropdownMinWidth={SHIPPING_COMPANY_LIST_WIDTH}
              dropdownPlacement="start"
              value={field.value ?? null}
              disabled={!canEditOrder}
              error={hasFieldError(fieldState, fieldsWithError, 'shippingCompanyId')}
              helperText={fieldState.error?.message}
              initialData={condition.shippingCompany ? [condition.shippingCompany] : []}
              getOptionLabel={(option) =>
                `${option.code} - ${option.commercialName || option.companyName} ${formatCNPJ(option.cnpj)}`
              }
              onBlur={field.onBlur}
              onChange={(value) => {
                field.onChange(value);
                onSave();
              }}
            />
          )}
        />

        <Controller
          name="shippingMethod"
          control={control}
          render={({ field, fieldState }) => (
            <TextField
              label={translate(LangEnum.GLOBAL_SHIPPING_MODALITY)}
              value={field.value ?? ''}
              disabled={!canEditOrder}
              error={Boolean(fieldState.error)}
              helperText={fieldState.error?.message}
              onChange={field.onChange}
              onBlur={() => onSave()}
            />
          )}
        />
      </Box>

      <Box sx={gridSx}>
        <Controller
          name="estimatedShippingBusinessDays"
          control={control}
          render={({ field, fieldState }) => (
            <TextField
              type="number"
              label={translate(LangEnum.GLOBAL_DELIVERY_TERM)}
              slotProps={{ htmlInput: { min: MIN_DELIVERY_TERM_BUSINESS_DAYS, step: 1 } }}
              value={field.value ?? ''}
              disabled={!canEditOrder}
              error={Boolean(fieldState.error)}
              helperText={fieldState.error?.message}
              onChange={(event) =>
                field.onChange(event.target.value === '' ? null : Number(event.target.value))
              }
              onBlur={() => onSave()}
            />
          )}
        />

        <Controller
          name="shippingCostValue"
          control={control}
          render={({ field, fieldState }) => (
            <MoneyField
              label={translate(LangEnum.GLOBAL_SHIPPING_COST)}
              value={field.value}
              disabled={!canEditOrder}
              error={hasFieldError(fieldState, fieldsWithError, 'shippingCostValue')}
              helperText={fieldState.error?.message}
              onChange={field.onChange}
              onBlur={() => onSave()}
            />
          )}
        />
      </Box>
    </Stack>
  );
};

export default OrderConditionManualFreightFields;
